if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
        this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
        this.zoomClickHandler = this.zoomClickHandler || this.handleZoomClick.bind(this);
        window.addEventListener('on:debounced-resize', this.zoomInitHandler);
        this.initZoom();
        this.zoomLinks.forEach((el) => {
          el.removeEventListener('click', this.zoomClickHandler);
          el.addEventListener('click', this.zoomClickHandler);
        });
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        this.customSetActiveMedia(variantMedia, true);
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomImage = el.querySelector('.zoom-image');
        const productImage = el.querySelector('.product-image');
        if (!zoomImage || !productImage) {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
          return;
        }

        const zoomWidth = Number(zoomImage.dataset.originalWidth || 0);
        const imageWidth = productImage.getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        }
      });
    }

    /**
     * Handles clicks on zoomable product images.
     * @param {Event} evt - Event object.
     */
    handleZoomClick(evt) {
      evt.preventDefault();
      this.setFullscreenLinks();

      this.fullscreenIndex = this.fullscreenLinks.indexOf(evt.currentTarget);
      if (this.fullscreenIndex < 0) this.fullscreenIndex = 0;

      this.openFullscreenMedia();
    }

    /**
     * Stores the product images currently available in the gallery.
     */
    setFullscreenLinks() {
      this.fullscreenLinks = Array.from(this.zoomLinks).filter((link) => {
        const item = link.closest('.media-viewer__item');
        return item && item.clientWidth > 0 && item.dataset.mediaType === 'image';
      });

      if (!this.fullscreenLinks.length) {
        this.fullscreenLinks = Array.from(this.zoomLinks);
      }
    }

    /**
     * Creates the fullscreen product media viewer.
     */
    createFullscreenMedia() {
      if (this.fullscreenMedia) return;

      this.fullscreenMedia = document.createElement('div');
      this.fullscreenMedia.className = 'product-media-fullscreen';
      this.fullscreenMedia.hidden = true;
      this.fullscreenMedia.setAttribute('role', 'dialog');
      this.fullscreenMedia.setAttribute('aria-modal', 'true');
      this.fullscreenMedia.setAttribute('aria-label', 'Product image preview');

      this.fullscreenViewport = document.createElement('div');
      this.fullscreenViewport.className = 'product-media-fullscreen__viewport';

      this.fullscreenTrack = document.createElement('div');
      this.fullscreenTrack.className = 'product-media-fullscreen__track';
      this.fullscreenViewport.appendChild(this.fullscreenTrack);

      this.fullscreenImage = document.createElement('img');
      this.fullscreenImage.className = 'product-media-fullscreen__image';
      this.fullscreenViewport.appendChild(this.fullscreenImage);

      this.fullscreenClose = document.createElement('button');
      this.fullscreenClose.className = 'product-media-fullscreen__btn product-media-fullscreen__close';
      this.fullscreenClose.type = 'button';
      this.fullscreenClose.textContent = 'x';
      this.fullscreenClose.setAttribute('aria-label', 'Close image preview');

      this.fullscreenPrev = document.createElement('button');
      this.fullscreenPrev.className = 'product-media-fullscreen__btn product-media-fullscreen__prev';
      this.fullscreenPrev.type = 'button';
      this.fullscreenPrev.textContent = '<';
      this.fullscreenPrev.setAttribute('aria-label', 'Previous image');

      this.fullscreenNext = document.createElement('button');
      this.fullscreenNext.className = 'product-media-fullscreen__btn product-media-fullscreen__next';
      this.fullscreenNext.type = 'button';
      this.fullscreenNext.textContent = '>';
      this.fullscreenNext.setAttribute('aria-label', 'Next image');

      this.fullscreenCounter = document.createElement('div');
      this.fullscreenCounter.className = 'product-media-fullscreen__counter';

      this.fullscreenMedia.append(
        this.fullscreenViewport,
        this.fullscreenClose,
        this.fullscreenPrev,
        this.fullscreenNext,
        this.fullscreenCounter
      );
      document.body.appendChild(this.fullscreenMedia);

      this.fullscreenKeyHandler = this.fullscreenKeyHandler || this.handleFullscreenKeydown.bind(this);
      this.fullscreenScrollHandler = this.fullscreenScrollHandler || this.handleFullscreenScroll.bind(this);
      this.fullscreenClose.addEventListener('click', this.closeFullscreenMedia.bind(this));
      this.fullscreenPrev.addEventListener('click', () => this.showFullscreenMedia(this.fullscreenIndex - 1));
      this.fullscreenNext.addEventListener('click', () => this.showFullscreenMedia(this.fullscreenIndex + 1));
      this.fullscreenViewport.addEventListener('scroll', this.fullscreenScrollHandler);
      this.fullscreenMedia.addEventListener('click', (evt) => {
        if (evt.target === this.fullscreenMedia || evt.target === this.fullscreenViewport) {
          this.closeFullscreenMedia();
        }
      });
    }

    /**
     * Opens the fullscreen product media viewer.
     */
    openFullscreenMedia() {
      this.createFullscreenMedia();
      this.renderFullscreenTrack();
      this.showFullscreenMedia(this.fullscreenIndex);
      this.fullscreenMedia.hidden = false;
      if (!theme.mediaMatches.md && this.fullscreenTrack) {
        const activeTrackItem = this.fullscreenTrack.children[this.fullscreenIndex];
        if (activeTrackItem) activeTrackItem.scrollIntoView({ block: 'start' });
      }
      document.documentElement.classList.add('product-media-fullscreen-open');
      document.addEventListener('keydown', this.fullscreenKeyHandler);
      this.fullscreenClose.focus();
    }

    /**
     * Closes the fullscreen product media viewer.
     */
    closeFullscreenMedia() {
      if (!this.fullscreenMedia) return;

      this.fullscreenMedia.hidden = true;
      document.documentElement.classList.remove('product-media-fullscreen-open');
      document.removeEventListener('keydown', this.fullscreenKeyHandler);
    }

    /**
     * Renders the mobile vertical fullscreen image carousel.
     */
    renderFullscreenTrack() {
      if (!this.fullscreenTrack) return;

      this.fullscreenTrack.innerHTML = '';
      this.fullscreenLinks.forEach((link, index) => {
        const productImage = link.querySelector('.product-image');
        const item = document.createElement('div');
        item.className = 'product-media-fullscreen__track-item';
        item.dataset.index = index;

        const trackImage = document.createElement('img');
        trackImage.src = link.dataset.fullscreenSrc || link.href;
        trackImage.alt = productImage ? productImage.alt : '';
        trackImage.loading = index === this.fullscreenIndex ? 'eager' : 'lazy';

        item.appendChild(trackImage);
        this.fullscreenTrack.appendChild(item);
      });
    }

    /**
     * Syncs the counter while customers scroll the mobile vertical carousel.
     */
    handleFullscreenScroll() {
      if (theme.mediaMatches.md) return;
      if (!this.fullscreenTrack || !this.fullscreenLinks.length || !this.fullscreenMedia || this.fullscreenMedia.hidden) return;

      const scrollTop = this.fullscreenViewport.scrollTop;
      const viewportCenter = scrollTop + (this.fullscreenViewport.clientHeight / 2);
      const items = Array.from(this.fullscreenTrack.children);
      const nextIndex = items.reduce((closestIndex, item, index) => {
        const itemCenter = item.offsetTop + (item.offsetHeight / 2);
        const closestItem = items[closestIndex];
        const closestCenter = closestItem.offsetTop + (closestItem.offsetHeight / 2);
        return Math.abs(itemCenter - viewportCenter) < Math.abs(closestCenter - viewportCenter)
          ? index
          : closestIndex;
      }, 0);
      if (nextIndex < 0 || nextIndex >= this.fullscreenLinks.length || nextIndex === this.fullscreenIndex) return;

      this.fullscreenIndex = nextIndex;
      this.fullscreenCounter.textContent = `${this.fullscreenIndex + 1} / ${this.fullscreenLinks.length}`;
    }

    /**
     * Shows a product image in the fullscreen viewer.
     * @param {number} index - Image index to show.
     */
    showFullscreenMedia(index) {
      if (!this.fullscreenLinks.length || this.fullscreenAnimating) return;

      const previousIndex = this.fullscreenIndex;
      this.fullscreenIndex = (index + this.fullscreenLinks.length) % this.fullscreenLinks.length;

      const link = this.fullscreenLinks[this.fullscreenIndex];
      const image = link.querySelector('.product-image');
      const nextSrc = link.dataset.fullscreenSrc || link.href;
      const nextAlt = image ? image.alt : '';
      this.fullscreenCounter.textContent = `${this.fullscreenIndex + 1} / ${this.fullscreenLinks.length}`;

      const showControls = this.fullscreenLinks.length > 1;
      this.fullscreenPrev.hidden = !showControls;
      this.fullscreenNext.hidden = !showControls;
      this.fullscreenCounter.hidden = !showControls;

      if (!theme.mediaMatches.md && this.fullscreenTrack && this.fullscreenMedia && !this.fullscreenMedia.hidden) {
        const activeTrackItem = this.fullscreenTrack.children[this.fullscreenIndex];
        if (activeTrackItem) activeTrackItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      if (typeof previousIndex !== 'number' || previousIndex === this.fullscreenIndex || !this.fullscreenImage.src) {
        this.fullscreenImage.src = nextSrc;
        this.fullscreenImage.alt = nextAlt;
        return;
      }

      const direction = index > previousIndex ? 'next' : 'prev';
      const incomingImage = document.createElement('img');
      incomingImage.className = `product-media-fullscreen__image product-media-fullscreen__image--incoming product-media-fullscreen__image--incoming-${direction}`;
      incomingImage.src = nextSrc;
      incomingImage.alt = nextAlt;
      this.fullscreenViewport.appendChild(incomingImage);
      this.fullscreenAnimating = true;

      const runAnimation = () => {
        requestAnimationFrame(() => {
          this.fullscreenImage.classList.add(`product-media-fullscreen__image--outgoing-${direction}`);
          incomingImage.classList.add('product-media-fullscreen__image--active');
        });

        window.setTimeout(() => {
          this.fullscreenImage.remove();
          incomingImage.className = 'product-media-fullscreen__image';
          this.fullscreenImage = incomingImage;
          this.fullscreenAnimating = false;
        }, 500);
      };

      if (incomingImage.decode) {
        incomingImage.decode().catch(() => {}).finally(runAnimation);
      } else {
        runAnimation();
      }
    }

    /**
     * Handles keyboard controls for the fullscreen viewer.
     * @param {KeyboardEvent} evt - Event object.
     */
    handleFullscreenKeydown(evt) {
      if (evt.key === 'Escape') {
        this.closeFullscreenMedia();
      } else if (evt.key === 'ArrowLeft') {
        this.showFullscreenMedia(this.fullscreenIndex - 1);
      } else if (evt.key === 'ArrowRight') {
        this.showFullscreenMedia(this.fullscreenIndex + 1);
      }
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
